import React, { useEffect, useState } from "react";
import "./login.scss";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import logoMark from "../assets/logo-mark.png";
import { useGetMeQuery, useLoginMutation } from "../../services/auth";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTE, IS_HR_SITE, isHrRole } from "../../config/access";

const SITE_MISMATCH_MESSAGE = IS_HR_SITE
  ? "This portal is for HR accounts only. Please use the main admin portal."
  : "HR accounts must sign in at hr.feelixconnection.com.";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [siteError, setSiteError] = useState("");
  const [login, { isLoading, error }] = useLoginMutation();
  const { data: user, refetch } = useGetMeQuery(null, {
    skip: !localStorage.getItem("token"),
  });
  const navigate = useNavigate();

  const clearSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("role");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSiteError("");
    try {
      const response = await login({ email, password }).unwrap();
      if (IS_HR_SITE !== isHrRole(response.admin.role)) {
        clearSession();
        setSiteError(SITE_MISMATCH_MESSAGE);
        return;
      }
      localStorage.setItem("token", response.token);
      localStorage.setItem("refreshToken", response.refreshToken);
      localStorage.setItem("role", response.admin.role);
      refetch();
      navigate(DEFAULT_ROUTE);
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (IS_HR_SITE !== isHrRole(user?.user?.role)) {
      clearSession();
      setSiteError(SITE_MISMATCH_MESSAGE);
      return;
    }
    navigate(DEFAULT_ROUTE);
  }, [user, navigate]);

  return (
    <div className="login-main">
      <div className="login-card">
        <img className="login-logo" src={logoMark} alt="Feelix" />
        <p className="title">Welcome Back</p>
        <p className="sub-title">
          {IS_HR_SITE ? "Login to the HR portal" : "Login to continue to your account"}
        </p>

        <form onSubmit={handleSubmit}>
          <label className="field-label" htmlFor="login-email">
            Email Address
          </label>
          <div className="input-wrapper">
            <MailOutlinedIcon fontSize="small" />
            <input
              id="login-email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <label className="field-label" htmlFor="login-password">
            Password
          </label>
          <div className="input-wrapper">
            <LockOutlinedIcon fontSize="small" />
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="visibility-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </button>
          </div>

          {siteError && <p className="error-message">{siteError}</p>}
          {!siteError && error && (
            <p className="error-message">
              {error?.data?.message || "Login failed. Please try again."}
            </p>
          )}

          <button className="login-btn" type="submit" disabled={isLoading}>
            {isLoading ? "Logging in…" : "Login Now"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
